import {
  SimpleGrid,
  Heading,
  Text,
  Button,
  Divider,
  Stack,
  Flex,
  Badge,
  Box,
} from "@chakra-ui/react";
import { CheckIcon, QuestionIcon } from "@chakra-ui/icons";
import { Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import WidgetWrapper from "../../../components/Widgets/WidgetWrapper";

import { useNavigate } from "react-router-dom";

const plans = [
  {
    titleKey: "price_plan.mini_title", // Changed to translation key
    subtitleKey: "price_plan.mini_subtitle", // Changed to translation key
    price: "249",
    featureTitleKey: "price_plan.mini_feature_title", // Changed to translation key
    features: [
      { textKey: "price_plan.feature_10_qr" }, // Changed to translation key
      { textKey: "price_plan.feature_unlimited_scans" }, // Changed to translation key
      { textKey: "price_plan.feature_analytics_access" }, // Changed to translation key
    ],
    buttonTextKey: "price_plan.choose_mini", // Changed to translation key
    highlight: false,
  },

  {
    titleKey: "price_plan.large_title", // Changed to translation key
    subtitleKey: "price_plan.large_subtitle", // Changed to translation key
    price: "1 299",
    featureTitleKey: "price_plan.large_feature_title", // Changed to translation key
    features: [
      { textKey: "price_plan.feature_100_qr" }, // Changed to translation key
      {
        textKey: "price_plan.feature_custom_subdomain", // Changed to translation key
        questionKey: "price_plan.feature_custom_subdomain_question", // Added translation key for the question
      },
      {
        textKey: "price_plan.feature_smart_plings", // Changed to translation key
        questionKey: "price_plan.feature_smart_plings_question", // Added translation key for the question
      },
      {
        textKey: "price_plan.feature_export_data", // Changed to translation key
        questionKey: "price_plan.feature_export_data_question", // Added translation key for the question
      },
      { textKey: "price_plan.feature_personal_consulting" }, // Changed to translation key
    ],
    buttonTextKey: "price_plan.choose_large", // Changed to translation key
    highlight: true,
  },
  {
    titleKey: "price_plan.enterprise_title", // Changed to translation key
    subtitleKey: "price_plan.enterprise_subtitle", // Changed to translation key
    featureTitleKey: "price_plan.enterprise_feature_title", // Changed to translation key
    features: [
      { textKey: "price_plan.feature_full_solution" }, // Changed to translation key
      { textKey: "price_plan.feature_custom_integrations" }, // Changed to translation key
      { textKey: "price_plan.feature_unmatched_support" }, // Changed to translation key
    ],
    buttonTextKey: "price_plan.contact_us", // Changed to translation key
    buttonHighlight: true,
    highlight: false,
  },
];

export default function PricingTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <SimpleGrid gap={5} autoRows={"auto 1fr"}>
      <WidgetWrapper
        child={
          <Box width={"100%"}>
            <Heading as="h2" size="md" mb={2}>
              {t("price_plan.price_title")}
            </Heading>
            <Text>{t("price_plan.price_subtitle_text")}</Text>
          </Box>
        }
      />
      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap={5}
        mb="auto"
        color={"black"}
      >
        {plans.map((plan, index) => {
          return (
            <Stack key={index} position="relative" height="100%">
              {plan.highlight && (
                <Badge
                  colorScheme="purple"
                  position="absolute"
                  top="-10px"
                  left="50%"
                  transform="translateX(-50%)"
                  zIndex={1}
                  bg={"#5138ee"}
                  color={"white"}
                  px={"10px"}
                  py="2px"
                >
                  {t("price_plan.most_popular")} {/* Translated text */}
                </Badge>
              )}
              <Box
                border={
                  plan.highlight ? "2px solid #5138ee" : "1px solid #E2E8F0"
                }
                borderRadius="xl"
                boxShadow={plan.highlight ? "lg" : "sm"}
                p={5}
                height="100%"
                bg={plan.highlight ? "gray.50" : "white"}
              >
                <Stack gap={6} height="100%" mb="20px">
                  <Stack>
                    <Heading as="h1" fontSize={"xl"} fontWeight={"normal"}>
                      {t(plan.titleKey)} {/* Translated title */}
                    </Heading>
                    <Flex gap={1}>
                      <Text fontSize={"2xl"} fontWeight={"bold"}>
                        {plan.price} {plan.price && "kr"}
                      </Text>
                      {plan.price ? (
                        <Text
                          fontWeight={"semibold"}
                          fontSize={"sm"}
                          color={"#646464"}
                          mt="auto"
                          mb="4px"
                        >
                          /{t("price_plan.per_month")} {/* Translated text */}
                        </Text>
                      ) : (
                        <Box mt="14px"></Box>
                      )}
                    </Flex>
                    <Text fontSize="sm" color="#646464" mb="2">
                      {t(plan.subtitleKey)} {/* Translated subtitle */}
                    </Text>
                  </Stack>
                  <Divider />
                  <Stack gap={4}>
                    <Text fontSize={"sm"} fontWeight={"semibold"}>
                      {t(plan.featureTitleKey)} {/* Translated feature title */}
                    </Text>
                    {plan.features.map((feature, featureIndex) => (
                      <Flex key={featureIndex} gap={2}>
                        <CheckIcon
                          color={"#5138ee"}
                          fontSize={"xs"}
                          my={"auto"}
                        />
                        <Text fontSize={"sm"} color={"#646464"}>
                          {t(feature.textKey)} {/* Translated feature text */}
                        </Text>
                        {feature.questionKey && (
                          <Tooltip
                            label={t(feature.questionKey)}
                            fontSize="xs"
                            p={"10px"}
                            bg="#292929"
                          >
                            <QuestionIcon
                              fontSize={"sm"}
                              my="auto"
                              cursor="pointer"
                            />
                          </Tooltip>
                        )}
                      </Flex>
                    ))}
                  </Stack>
                  <Button
                    mt="auto"
                    bg={
                      plan.highlight || plan.buttonHighlight
                        ? "#5138ee"
                        : "#edf2f6"
                    }
                    color={
                      plan.highlight || plan.buttonHighlight ? "white" : "black"
                    }
                    onClick={() =>
                      navigate(
                        `/support/kontakt?q=` +
                          t("i_want_to_buy_title") +
                          t(plan.titleKey) // Translated button text
                      )
                    }
                  >
                    {t(plan.buttonTextKey)} {/* Translated button text */}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          );
        })}
      </SimpleGrid>
    </SimpleGrid>
  );
}
